import { useMemo, useState } from "react";
import {
  Loading,
  SummaryCard,
  Button,
  ClinicFilterBar,
  getCurrentMonth,
  usePersistedState,
  showSuccess,
  showApiError,
  ConfirmModal,
} from "@common";
import { formatMoney, canManageSchedulePayments } from "@utils";
import {
  useBankAccounts,
  useStatementPayments,
  useCreateStatementPayment,
  useUpdateStatementPayment,
  useDeleteStatementPayment,
  useStatementPlans,
  useCreateStatementPlan,
  useUpdateStatementPlan,
  useDeleteStatementPlan,
} from "@api/hooks";
import { useAuth } from "@api/useAuth";
import useClinicFilterOptions from "@hooks/useClinicFilterOptions";
import CurrencyBadge from "../CurrencyBadge";
import StatementPaymentModal from "./StatementPaymentModal";
import StatementPlanModal from "./StatementPlanModal";
import { PAYMENT_PLANS, deriveStatementDates, planToFormValues, parseISO } from "./statementPlan";

const PLAN_LABELS = Object.fromEntries(PAYMENT_PLANS.map((p) => [p.value, p.label]));

// ISO string -> "Jul 24, 2026". Parsed as LOCAL midnight, not UTC, so the date
// shown is the date stored rather than one day earlier.
function fmtDate(iso) {
  const d = parseISO(iso);
  if (!d) return "—";
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Who owns the card, for the line under its label. A BankAccount belongs to
 * EITHER a clinic or an entity, never both, so one lookup serves both modes —
 * clinic-owned Visas on the clinic Cash Flow page, entity-level cards on Dens N
 * Dente — and neither has to be told which mode it is rendering in.
 */
function ownerNameOf(row) {
  return row?.clinic_name ?? row?.entity_name ?? "—";
}

const TABLE_COLS = [
  { key: "card", label: "Card" },
  { key: "from", label: "Paid from" },
  { key: "cycle", label: "Statement → Due" },
  { key: "plan", label: "Payment plan" },
  { key: "payments", label: "Payments" },
  { key: "balance", label: "Balance" },
  { key: "actions", label: "" },
];

// Not entered first — the actionable half, which empties as the month is worked
// through. Colour matches the meaning already used elsewhere on this page: amber
// for "needs attention", emerald for "done".
const SECTIONS = [
  {
    kind: "plan",
    label: "Not entered",
    headerClass: "bg-palette-amber-soft text-palette-amber-heading",
  },
  {
    kind: "statement",
    label: "Entered",
    headerClass: "bg-palette-emerald-soft text-palette-emerald-text",
  },
];

function StatementTable({ rows, month, onEdit, onDelete, onAdd, deletingId }) {
  return (
    <table className="cf-table w-full border-collapse text-sm">
      <thead>
        <tr className="border-b border-palette-gray-divider bg-palette-gray-subtle">
          {TABLE_COLS.map((col) => (
            <th
              key={col.key}
              className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider text-palette-gray-secondary whitespace-nowrap ${
                col.key === "balance" || col.key === "actions" ? "text-right" : "text-left"
              }`}
            >
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      {/* Split into Not entered / Entered rather than one list sorted by card.
          The tab answers "what still needs doing this month", and that is a
          property of the row, not of the card's name — mixing the two states meant
          scanning every row to find the outstanding ones.

          Not entered comes FIRST: it is the actionable half, and it empties as the
          month is worked through. An empty section is dropped entirely rather than
          shown with a zero.

          Two row shapes, one column layout. A card with this month's statement
          shows the statement; one still on its plan alone shows what the plan WOULD
          create, so Add confirms something visible rather than opening a blank form.

          No row-level onClick: editing is an explicit button, since a clickable row
          next to a destructive Remove turns a mis-aimed click into the wrong
          action. */}
      {SECTIONS.map((section) => {
        const sectionRows = rows.filter((r) => r.kind === section.kind);
        if (!sectionRows.length) return null;
        return (
          <tbody key={section.kind}>
            <tr className={`border-b border-palette-gray-divider ${section.headerClass}`}>
              <td
                colSpan={TABLE_COLS.length}
                className="px-4 py-2 text-xs font-bold uppercase tracking-wider"
              >
                {section.label}
                <span className="ml-2 font-medium normal-case tracking-normal opacity-75">
                  {sectionRows.length} card{sectionRows.length === 1 ? "" : "s"}
                </span>
              </td>
            </tr>
            {sectionRows.map((r) => {
              const entered = r.kind === "statement";
              const st = r.statement;
              const plan = r.plan;
              // A plan row has no dates of its own — these are the ones it will produce
              // for the month on screen.
              const derived = entered ? null : deriveStatementDates(plan, month);
              const cardLabel = entered ? st.cardLabel : plan.to_card_account_label;
              const fromLabel = entered ? st.fromLabel : plan.from_bank_account_label;
              const currency = entered ? st.currency : plan.currency;
              const planKey = entered ? st.plan : plan.payment_plan;
              const planCount = entered ? st.planCount : plan.plan_count;
              return (
                <tr key={r.key} className="border-b border-palette-gray-divider last:border-0">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="truncate font-semibold text-palette-gray-primary">
                        {cardLabel}
                      </span>
                      {currency && <CurrencyBadge currency={currency} />}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-palette-gray-secondary">
                      {entered ? st.ownerName : ownerNameOf(plan)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="block max-w-[220px] truncate text-palette-gray-heading">
                      {fromLabel}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-palette-gray-heading">
                    {entered ? (
                      <>
                        {fmtDate(st.statementDate)} → {fmtDate(st.dueDate)}
                      </>
                    ) : derived?.statementDate ? (
                      <>
                        <span className="text-palette-gray-secondary">
                          {fmtDate(derived.statementDate)} → {fmtDate(derived.dueDate)}
                        </span>
                        <span className="ml-1.5 text-xs text-palette-gray-placeholder">
                          from plan
                        </span>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-palette-gray-heading">
                    {PLAN_LABELS[planKey] ?? planKey}
                    {planCount ? ` (${planCount})` : ""}
                  </td>
                  {/* Every payment date, not just the first. Showing "N × from <first
                  date>" hid any move of a later payment — the row looked
                  unchanged after rescheduling it on the cash flow. */}
                  <td
                    className="px-4 py-3 text-palette-gray-secondary"
                    title={
                      entered
                        ? st.splits.map((x) => `${fmtDate(x.date)} — ${x.amount}`).join("\n")
                        : undefined
                    }
                  >
                    {entered && st.splits.length ? (
                      <span className="flex flex-wrap gap-x-1.5 gap-y-0.5">
                        {st.splits.map((x, i) => (
                          <span key={i} className="whitespace-nowrap">
                            {fmtDate(x.date)}
                            {i < st.splits.length - 1 ? "," : ""}
                          </span>
                        ))}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-palette-gray-primary whitespace-nowrap">
                    {entered ? (
                      formatMoney(st.balance)
                    ) : (
                      <span className="font-normal text-palette-gray-placeholder">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-2">
                      {entered ? (
                        <>
                          {onEdit && (
                            <Button variant="primary" size="xs" onClick={() => onEdit(st)}>
                              Update
                            </Button>
                          )}
                          {onDelete && (
                            <Button
                              variant="danger"
                              size="xs"
                              onClick={() => onDelete(st)}
                              disabled={deletingId === st.id}
                            >
                              {deletingId === st.id ? "Removing…" : "Remove"}
                            </Button>
                          )}
                        </>
                      ) : (
                        onAdd && (
                          <Button
                            variant="primary"
                            size="xs"
                            onClick={() => onAdd(plan)}
                            title="Open this month's statement pre-filled from the plan — enter the amount"
                          >
                            Add
                          </Button>
                        )
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        );
      })}
    </table>
  );
}

const PLAN_TABLE_COLS = [
  { key: "card", label: "Card" },
  { key: "from", label: "Paid from" },
  { key: "cycle", label: "Statement → Due day" },
  { key: "plan", label: "Payment plan" },
  { key: "actions", label: "" },
];

/** Ordinal for a day-of-month: 1 → 1st, 22 → 22nd. */
function ordinal(day) {
  const n = Number(day);
  if (!n) return "—";
  const suffix = n % 100 >= 11 && n % 100 <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] || "th";
  return `${n}${suffix}`;
}

/**
 * Saved plans — one per card, no dates and no balance.
 *
 * Read-only apart from Remove. A plan is WRITTEN by "Save + set default" in the
 * statement modal, where every field already exists and the resulting split can
 * be previewed; a second editor here would be the same form twice, and one of the
 * two would drift.
 */
function PlanTable({ plans, onEdit, onDelete, deletingId }) {
  return (
    <table className="cf-table w-full border-collapse text-sm">
      <thead>
        <tr className="border-b border-palette-gray-divider bg-palette-gray-subtle">
          {PLAN_TABLE_COLS.map((col) => (
            <th
              key={col.key}
              className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider text-palette-gray-secondary whitespace-nowrap ${
                col.key === "actions" ? "text-right" : "text-left"
              }`}
            >
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {plans.map((p) => (
          <tr key={p.id} className="border-b border-palette-gray-divider last:border-0">
            <td className="px-4 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <span className="truncate font-semibold text-palette-gray-primary">
                  {p.to_card_account_label}
                </span>
                {p.currency && <CurrencyBadge currency={p.currency} />}
              </div>
              <div className="mt-0.5 truncate text-xs text-palette-gray-secondary">
                {ownerNameOf(p)}
              </div>
            </td>
            <td className="px-4 py-3">
              <span className="block max-w-[220px] truncate text-palette-gray-heading">
                {p.from_bank_account_label}
              </span>
            </td>
            <td className="px-4 py-3 whitespace-nowrap text-palette-gray-heading">
              {ordinal(p.statement_day)} → {ordinal(p.due_day)}
              <span className="ml-1 text-xs text-palette-gray-secondary">
                {p.due_month_offset === 0
                  ? "same month"
                  : p.due_month_offset === 1
                    ? "next month"
                    : `+${p.due_month_offset} months`}
              </span>
            </td>
            <td className="px-4 py-3 whitespace-nowrap text-palette-gray-heading">
              {PLAN_LABELS[p.payment_plan] ?? p.payment_plan}
              {p.plan_count ? ` (${p.plan_count})` : ""}
            </td>
            <td className="px-4 py-3 text-right whitespace-nowrap">
              {/* The shared Button rather than bare markup, so these inherit the
                  app's focus rings and disabled styling. `xs` keeps a row action
                  from outweighing the row it belongs to. */}
              <div className="flex items-center justify-end gap-2">
                {onEdit && (
                  <Button variant="primary" size="xs" onClick={() => onEdit(p)}>
                    Update
                  </Button>
                )}
                {onDelete && (
                  <Button
                    variant="danger"
                    size="xs"
                    onClick={() => onDelete(p)}
                    disabled={deletingId === p.id}
                  >
                    {deletingId === p.id ? "Removing…" : "Remove"}
                  </Button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PlansEmptyState({ canEdit, awaitingSelection, noun = "clinics" }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-palette-gray-divider bg-white py-20 text-center">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-palette-gray-subtle">
        <svg
          className="h-6 w-6 text-palette-gray-placeholder"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <p className="text-sm font-semibold text-palette-gray-heading">
        {awaitingSelection
          ? `Select ${noun === "entities" ? "an entity" : "a clinic"} to begin`
          : "No saved plans yet"}
      </p>
      {!awaitingSelection && canEdit && (
        <p className="mt-1 max-w-md text-sm text-palette-gray-placeholder">
          Fill in a statement, then use <span className="font-medium">Save + set default</span> to
          remember its shape for this card. Next month you will only enter the amount.
        </p>
      )}
    </div>
  );
}

function EmptyState({ canEdit, awaitingSelection, noun = "clinics" }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-palette-gray-divider bg-white py-20 text-center">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-palette-gray-subtle">
        <svg
          className="h-6 w-6 text-palette-gray-placeholder"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z"
          />
        </svg>
      </div>
      {/* Before anything is picked NO request has been made, so "no statements"
          would be a claim the page has not checked. Same distinction the All
          Payments tab draws. */}
      <p className="text-sm font-semibold text-palette-gray-heading">
        {awaitingSelection
          ? `Select ${noun === "entities" ? "an entity" : "a clinic"} to begin`
          : "No statements yet"}
      </p>
      {awaitingSelection ? (
        <p className="mt-1 text-sm text-palette-gray-placeholder">
          Pick {noun === "entities" ? "an entity" : "a clinic"} above to see its card statements.
        </p>
      ) : (
        canEdit && (
          <p className="mt-1 text-sm text-palette-gray-placeholder">
            Add a statement to plan how a card balance is paid.
          </p>
        )
      )}
    </div>
  );
}

/**
 * "Monthly Inputs" tab — recurring credit-card statements and their payment
 * plans.
 *
 * Two scopes, chosen the same way the All Payments tab chooses: by whether
 * `entityIds` was passed.
 *
 *   CLINIC MODE (default, AccessClinicCashFlow) — clinic-owned accounts
 *     (`clinic_id=…`): the clinic Visas, paid from that clinic's bank accounts.
 *     Scoped by this tab's OWN Area Manager + Clinic picker, since the clinic
 *     Cash Flow shell holds no clinic selection to pass down.
 *   ENTITY MODE (DensNDenteCashFlow, `entityIds` given) — the entity-level
 *     cards, paid from entity-level bank accounts.
 *
 * A statement pays a card FROM a bank account, and both sides come from the
 * SAME scope: a clinic's Visa is offered its clinic's bank accounts, never an
 * entity's. `BankAccount` gives every account exactly one owner (clinic OR
 * entity), so the two account sets are disjoint by construction and neither
 * page can write a statement against the other's cards.
 *
 * Nothing is fetched until a scope exists — no clinic picked, no request —
 * because an unscoped list is every clinic's statements.
 */
export default function MonthlyInputs({
  canEdit: canEditProp,
  entityIds = null,
  // Entity mode only: the host page owns the entity selection and passes the
  // picker through, so Month + Entity render on one filter bar — same shape as
  // the All Payments tab. In clinic mode the picker below is this tab's own.
  entityOptions = [],
  entityValue = [],
  onEntityChange = null,
}) {
  const entityMode = Array.isArray(entityIds);
  const { user } = useAuth();
  const canEdit = canEditProp ?? canManageSchedulePayments(user);

  // Clinic mode's own scope. Persisted under this tab's keys rather than shared
  // with All Payments: the two lists answer different questions, and Monthly
  // Inputs is worked card-by-card while All Payments is scanned by clinic.
  const [areaManagerFilter, setAreaManagerFilter] = usePersistedState(
    "monthlyInputs:areaManagerFilter",
    "",
  );
  const [clinicFilters, setClinicFilters] = usePersistedState("monthlyInputs:clinicFilters", []);
  // `enabled: false` in entity mode — there are no clinic dropdowns to populate,
  // so the clinics list is not worth a request there.
  const { clinicsQuery, areaManagerOptions, clinicOptions } = useClinicFilterOptions(
    areaManagerFilter,
    { enabled: !entityMode },
  );

  // The endpoints take `clinic_id`, not the code the picker holds: an indexed
  // integer FK compare rather than a string compare on a joined clinics row.
  const selectedClinicIds = useMemo(() => {
    if (clinicFilters.length === 0) return [];
    const idByCode = new Map(clinicOptions.map((o) => [o.value, o.id]));
    return clinicFilters.map((code) => idByCode.get(code)).filter((id) => id != null);
  }, [clinicFilters, clinicOptions]);

  // Persisted separately from the All Payments tab: the two lists answer
  // different questions and there is no reason to move together.
  const [months, setMonths] = usePersistedState("monthlyInputs:months", () => [getCurrentMonth()]);
  const month = months[0] ?? null;

  // "statements" | "plans". Persisted like the month, so returning to the tab
  // lands where the user left it.
  const [tab, setTab] = usePersistedState("monthlyInputs:tab", () => "statements");

  // The plan being edited, and the plan awaiting delete confirmation. Separate,
  // because removing is destructive and must never share state with editing.
  const [editingPlan, setEditingPlan] = useState(null);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [statementToDelete, setStatementToDelete] = useState(null);
  // The plan an Add click is creating from. Distinct from `editing`: this is a
  // CREATE that starts pre-filled, not an edit of an existing statement.
  const [addingFromPlan, setAddingFromPlan] = useState(null);

  // The selected month bounds `statement_date` — statements ISSUED in it. Their
  // payments may well fall in later months; that is the plan, not the statement.
  const monthRange = useMemo(() => {
    if (!month) return {};
    const [y, m] = month.split("-").map(Number);
    const lastDay = new Date(y, m, 0).getDate();
    return { date_from: `${month}-01`, date_to: `${month}-${String(lastDay).padStart(2, "0")}` };
  }, [month]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // The one filter that distinguishes the two modes. Every query below spreads
  // it, so there is a single place that decides what "this page's scope" means.
  // `entity_id` / `clinic_id` each already guarantee the matching FK is non-null,
  // so no extra `has_clinic` filter is needed to keep the sets apart.
  const scopeParams = useMemo(
    () =>
      entityMode
        ? { entity_id: (entityIds ?? []).join(",") }
        : { clinic_id: selectedClinicIds.join(",") },
    [entityMode, entityIds, selectedClinicIds],
  );
  // No scope, no request: an unfiltered list is every clinic's statements.
  const scopeReady = entityMode ? (entityIds ?? []).length > 0 : selectedClinicIds.length > 0;

  // Both sides come from the SAME scope, split by account_type: cash leaves a
  // bank account, a credit card is what gets paid. So a clinic Visa is offered
  // its own clinic's bank accounts, and an entity card its entity's.
  const bankQuery = useBankAccounts(
    { status: "active", ...scopeParams, account_type: "bank", limit: 500 },
    { enabled: scopeReady },
  );
  const cardQuery = useBankAccounts(
    { status: "active", ...scopeParams, account_type: "credit_card", limit: 500 },
    { enabled: scopeReady },
  );

  const bankAccounts = useMemo(
    () => bankQuery.data?.results ?? bankQuery.data ?? [],
    [bankQuery.data],
  );
  const cardAccounts = useMemo(
    () => cardQuery.data?.results ?? cardQuery.data ?? [],
    [cardQuery.data],
  );

  const statementsQuery = useStatementPayments(
    { ...scopeParams, status: "active", limit: 500, ...monthRange },
    { enabled: scopeReady },
  );
  const createStatement = useCreateStatementPayment();
  const updateStatement = useUpdateStatementPayment();
  const deleteStatement = useDeleteStatementPayment();

  // Saved plans for the same scope. NOT bounded by the month — a plan has
  // no dates, which is the point of it; one plan serves every month.
  const plansQuery = useStatementPlans(
    { ...scopeParams, status: "active", limit: 500 },
    { enabled: scopeReady },
  );
  const createPlan = useCreateStatementPlan();
  const updatePlan = useUpdateStatementPlan();
  const deletePlan = useDeleteStatementPlan();

  const plans = useMemo(() => plansQuery.data?.results ?? plansQuery.data ?? [], [plansQuery.data]);

  const statements = useMemo(() => {
    const rows = statementsQuery.data?.results ?? statementsQuery.data ?? [];
    return rows.map((s) => ({
      id: s.id,
      // Clinic-owned or entity-level; exactly one is populated per row.
      ownerName: ownerNameOf(s),
      // Needed to pair a statement with its card's saved plan.
      cardAccountId: s.to_card_account,
      cardLabel: s.to_card_account_label,
      fromLabel: s.from_bank_account_label,
      currency: s.currency,
      statementDate: s.statement_date,
      dueDate: s.due_date,
      plan: s.payment_plan,
      planCount: s.plan_count,
      splits: Array.isArray(s.splits) ? s.splits : [],
      balance: s.balance != null ? Number(s.balance) : 0,
      isDefault: s.is_default,
      raw: s,
    }));
  }, [statementsQuery.data]);

  // The Statements tab, per CARD rather than per statement.
  //
  // A saved plan says "this card bills every month"; a statement says "and here is
  // the month's balance". So one row per card: the STATEMENT when it exists for the
  // selected month, otherwise the PLAN with an Add button. The statement REPLACES
  // the plan rather than sitting beside it — two rows for one card in one month
  // would be two answers to the same question.
  //
  // Union, not just plans: a statement whose card has no saved plan still has to
  // appear, or one entered outside the plan flow would vanish from the list.
  const monthlyRows = useMemo(() => {
    const byCard = new Map();
    plans.forEach((p) => {
      byCard.set(String(p.to_card_account), { kind: "plan", key: `plan-${p.id}`, plan: p });
    });
    statements.forEach((st) => {
      const cardKey = String(st.cardAccountId);
      byCard.set(cardKey, {
        kind: "statement",
        key: `stmt-${st.id}`,
        statement: st,
        // Kept so the row can note the card is on a plan even once entered.
        plan: byCard.get(cardKey)?.plan ?? null,
      });
    });
    const labelOf = (r) =>
      r.kind === "statement" ? r.statement.cardLabel : r.plan.to_card_account_label;
    return [...byCard.values()].sort((a, b) =>
      String(labelOf(a) ?? "").localeCompare(String(labelOf(b) ?? "")),
    );
  }, [plans, statements]);

  const totals = useMemo(() => {
    const balance = statements.reduce((s, x) => s + x.balance, 0);
    const payments = statements.reduce((s, x) => s + x.splits.length, 0);
    return { balance, payments, cards: new Set(statements.map((s) => s.cardLabel)).size };
  }, [statements]);

  // Opens the statement modal already filled from the card's plan, so the only
  // thing left is the amount.
  function openAddFromPlan(plan) {
    setEditing(null);
    setAddingFromPlan(plan);
    setModalOpen(true);
  }

  function openCreate() {
    setEditing(null);
    setAddingFromPlan(null);
    setModalOpen(true);
  }

  function openEdit(row) {
    setAddingFromPlan(null);
    setEditing(row.raw);
    setModalOpen(true);
  }

  // Map the stored row back onto the modal's form shape. `splits` repopulates
  // the custom rows so a custom plan reopens exactly as it was saved.
  const initialValues = useMemo(() => {
    // Plan-seeded create. The modal's own plan-apply effect then runs for this card
    // and sets its "filled from the saved plan" notice, so the two agree.
    if (!editing && addingFromPlan) return planToFormValues(addingFromPlan, month);
    if (!editing) return null;
    return {
      from_bank_account: editing.from_bank_account ?? "",
      to_card_account: editing.to_card_account ?? "",
      statement_date: editing.statement_date ?? "",
      due_date: editing.due_date ?? "",
      balance: editing.balance ?? "",
      payment_plan: editing.payment_plan ?? "full_on_due",
      plan_count: editing.plan_count ?? "",
      single_date:
        editing.payment_plan === "single_payment" ? (editing.splits?.[0]?.date ?? "") : "",
      custom: (editing.splits ?? []).length
        ? editing.splits.map((s) => ({ date: s.date, amount: s.amount }))
        : [{ date: "", amount: "" }],
      notes: editing.notes ?? "",
    };
  }, [editing, addingFromPlan, month]);

  async function handleSave(payload) {
    try {
      if (editing) {
        await updateStatement.mutateAsync({ id: editing.id, ...payload });
        showSuccess("Statement updated.");
      } else {
        await createStatement.mutateAsync(payload);
        showSuccess("Statement saved.");
      }
      setModalOpen(false);
      setEditing(null);
      setAddingFromPlan(null);
    } catch (err) {
      showApiError(err);
    }
  }

  // "Save + set default": the statement AND the reusable plan behind it.
  //
  // The statement goes first because it is the reason the user opened the modal.
  // The two writes are reported separately on purpose — if the plan write fails,
  // the statement is already saved, and a single error toast would read as though
  // nothing had been.
  //
  // `existingPlan` is the card's current plan, if any. One plan per card is
  // enforced by the database, so a second POST would be rejected; saving again has
  // to be a PATCH of that row.
  async function handleSaveWithPlan(payload, planPayload, existingPlan) {
    try {
      if (editing) {
        await updateStatement.mutateAsync({ id: editing.id, ...payload });
      } else {
        await createStatement.mutateAsync(payload);
      }
    } catch (err) {
      showApiError(err);
      return;
    }

    try {
      if (existingPlan) {
        await updatePlan.mutateAsync({ id: existingPlan.id, ...planPayload });
        showSuccess("Statement saved, and this card's plan updated.");
      } else {
        await createPlan.mutateAsync(planPayload);
        showSuccess("Statement saved. Next month, pick this card and enter the amount.");
      }
    } catch (err) {
      showSuccess("Statement saved.");
      showApiError(err);
    }

    setModalOpen(false);
    setEditing(null);
  }

  // Only reached from the confirmation dialog — the Remove button opens that, it
  // does not delete. A plan takes real thought to set up and there is no undo.
  // Reached only from the confirmation dialog. A statement carries a balance and a
  // resolved split that drive the cash-flow projection, and deleting it removes
  // those outflows — not something to do on a stray click.
  async function handleConfirmDeleteStatement() {
    if (!statementToDelete) return;
    try {
      await deleteStatement.mutateAsync(statementToDelete.id);
      showSuccess(`Statement for ${statementToDelete.cardLabel} removed.`);
      setStatementToDelete(null);
    } catch (err) {
      showApiError(err);
    }
  }

  async function handleConfirmDeletePlan() {
    if (!planToDelete) return;
    try {
      await deletePlan.mutateAsync(planToDelete.id);
      showSuccess(`Saved plan for ${planToDelete.to_card_account_label} removed.`);
      setPlanToDelete(null);
    } catch (err) {
      showApiError(err);
    }
  }

  // A plan edit is always a PATCH: the row exists, and one-per-card means a POST
  // would be rejected by the unique constraint anyway.
  async function handleUpdatePlan(payload) {
    try {
      await updatePlan.mutateAsync({ id: editingPlan.id, ...payload });
      showSuccess("Saved plan updated.");
      setEditingPlan(null);
    } catch (err) {
      showApiError(err);
    }
  }

  // Serialized plan -> the plan modal's form shape.
  const planInitialValues = useMemo(() => {
    if (!editingPlan) return null;
    return {
      from_bank_account: editingPlan.from_bank_account ?? "",
      to_card_account: editingPlan.to_card_account ?? "",
      statement_day: editingPlan.statement_day ?? "",
      due_day: editingPlan.due_day ?? "",
      due_month_offset: String(editingPlan.due_month_offset ?? 1),
      payment_plan: editingPlan.payment_plan ?? "full_on_due",
      plan_count: editingPlan.plan_count ?? "",
      status: editingPlan.status ?? "active",
      notes: editingPlan.notes ?? "",
    };
  }, [editingPlan]);

  // In clinic mode the queries stay DISABLED until a clinic is picked, and a
  // disabled query never reports isLoading — so the clinics fetch (which
  // populates the picker itself) has to be part of this, or the tab renders its
  // empty state with no way to make a selection yet.
  const isLoading =
    (!entityMode && clinicsQuery.isLoading) ||
    bankQuery.isLoading ||
    cardQuery.isLoading ||
    statementsQuery.isLoading;
  const noun = entityMode ? "entities" : "clinics";

  return (
    <>
      <div className="flex flex-wrap gap-3 mb-6">
        <SummaryCard label="Statement Balance" value={formatMoney(totals.balance)} />
        <SummaryCard label="Cards Covered" value={String(totals.cards)} />
        <SummaryCard label="Scheduled Payments" value={String(totals.payments)} />
      </div>

      {/* Month + scope, same shape as the All Payments tab: Entity in entity
          mode (no Area Manager and no clinic picker — an entity has neither),
          Area Manager + Clinic in clinic mode. */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        {entityMode ? (
          <ClinicFilterBar
            months={months}
            onMonthsChange={setMonths}
            showAreaManager={false}
            showClinic={!!onEntityChange}
            entityOptions={entityOptions}
            entityValue={entityValue}
            onEntityChange={onEntityChange ?? undefined}
          />
        ) : (
          <ClinicFilterBar
            months={months}
            onMonthsChange={setMonths}
            areaManagerFilter={areaManagerFilter}
            areaManagerOptions={areaManagerOptions}
            // Changing manager re-derives the clinic options, so a selection
            // made under the old one would silently filter everything out.
            onAreaManagerChange={(v) => {
              setAreaManagerFilter(v);
              setClinicFilters([]);
            }}
            clinicOptions={clinicOptions}
            clinicValue={clinicFilters}
            onClinicChange={setClinicFilters}
          />
        )}
        {canEdit && (
          <Button
            variant="primary"
            size="sm"
            className="gap-2 h-9 font-semibold shrink-0"
            onClick={openCreate}
            disabled={cardAccounts.length === 0}
            title={
              cardAccounts.length === 0
                ? `No credit card accounts for the selected ${noun}`
                : undefined
            }
          >
            <svg
              className="w-4 h-4 shrink-0"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M8 3v10M3 8h10" strokeLinecap="round" />
            </svg>
            Add Statement
          </Button>
        )}
      </div>

      {/* Statements vs saved plans. Two different questions about the same cards —
          "what is due this month" and "how is this card normally paid" — and the
          month filter above only applies to the first, which is why they are tabs
          rather than one merged list. */}
      <div className="mb-4 flex gap-1 border-b border-palette-gray-divider">
        {[
          { key: "statements", label: "Statements", count: monthlyRows.length },
          { key: "plans", label: "Saved plans", count: plans.length },
        ].map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                active
                  ? "border-palette-indigo-badge text-palette-indigo-text"
                  : "border-transparent text-palette-gray-secondary hover:text-palette-gray-heading"
              }`}
            >
              {t.label}
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                  active
                    ? "bg-palette-indigo-soft text-palette-indigo-badge"
                    : "bg-palette-gray-subtle text-palette-gray-secondary"
                }`}
              >
                {t.count}
              </span>
            </button>
          );
        })}
      </div>

      {tab === "statements" ? (
        isLoading || plansQuery.isLoading ? (
          <Loading text="Loading statements..." />
        ) : monthlyRows.length === 0 ? (
          <EmptyState canEdit={canEdit} awaitingSelection={!scopeReady} noun={noun} />
        ) : (
          <div className="rounded-2xl border border-palette-gray-divider bg-white shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <StatementTable
                rows={monthlyRows}
                month={month}
                onEdit={canEdit ? openEdit : undefined}
                onDelete={canEdit ? setStatementToDelete : undefined}
                onAdd={canEdit ? openAddFromPlan : undefined}
                deletingId={deleteStatement.isPending ? deleteStatement.variables : null}
              />
            </div>
          </div>
        )
      ) : plansQuery.isLoading ? (
        <Loading text="Loading saved plans..." />
      ) : plans.length === 0 ? (
        <PlansEmptyState canEdit={canEdit} awaitingSelection={!scopeReady} noun={noun} />
      ) : (
        <div className="rounded-2xl border border-palette-gray-divider bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <PlanTable
              plans={plans}
              onEdit={canEdit ? setEditingPlan : undefined}
              onDelete={canEdit ? setPlanToDelete : undefined}
              deletingId={deletePlan.isPending ? deletePlan.variables : null}
            />
          </div>
        </div>
      )}

      <StatementPaymentModal
        isOpen={canEdit && modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
          setAddingFromPlan(null);
        }}
        onSave={handleSave}
        saving={createStatement.isPending || updateStatement.isPending}
        bankAccounts={bankAccounts}
        cardAccounts={cardAccounts}
        initialValues={initialValues}
        isEdit={!!editing}
        plans={plans}
        // The month being viewed IS the month a new statement is for, so the
        // plan's day-of-month pair resolves against it.
        month={month}
        onSaveWithPlan={handleSaveWithPlan}
        savingPlan={createPlan.isPending || updatePlan.isPending}
      />

      <StatementPlanModal
        isOpen={canEdit && !!editingPlan}
        onClose={() => setEditingPlan(null)}
        onSave={handleUpdatePlan}
        saving={updatePlan.isPending}
        bankAccounts={bankAccounts}
        cardAccounts={cardAccounts}
        initialValues={planInitialValues}
        month={month}
      />

      <ConfirmModal
        isOpen={!!statementToDelete}
        onClose={() => setStatementToDelete(null)}
        onConfirm={handleConfirmDeleteStatement}
        title="Remove statement?"
        message={
          statementToDelete
            ? `The ${fmtDate(statementToDelete.statementDate)} statement for ${statementToDelete.cardLabel} (${formatMoney(statementToDelete.balance)}) will be permanently removed, along with its ${statementToDelete.splits.length} scheduled payment${statementToDelete.splits.length === 1 ? "" : "s"}. Those outflows will disappear from the cash flow. The card's saved plan is not affected.`
            : ""
        }
        confirmText="Remove statement"
        confirmVariant="danger"
        isLoading={deleteStatement.isPending}
      />

      <ConfirmModal
        isOpen={!!planToDelete}
        onClose={() => setPlanToDelete(null)}
        onConfirm={handleConfirmDeletePlan}
        title="Remove saved plan?"
        message={
          planToDelete
            ? `The saved plan for ${planToDelete.to_card_account_label} will be permanently removed. Statements already created from it are not affected — only future months stop being pre-filled.`
            : ""
        }
        confirmText="Remove plan"
        confirmVariant="danger"
        isLoading={deletePlan.isPending}
      />
    </>
  );
}
